import { Box, Divider, Typography } from '@mui/material';
import { rootSx } from './style';

export default function DataScienceGuideContent() {
  return (
    <Box className="DataScienceGuideContent-root" sx={rootSx}>
      <Box className="DataScienceGuideContent-main">
        <Typography className="DataScienceGuideContent-title">데이터 과학 설명</Typography>
        <Typography className="DataScienceGuideContent-subtitle">
          여러가지 종합된 데이터를 분석해서 유용한 결론을 찾는 과정
        </Typography>
      </Box>

      <Box className="DataScienceGuideContent-section">
        <Box className="DataScienceGuideContent-imageBox">
          <img src="/_static/images/data_science_1.png" width="100%" alt="" />
        </Box>
        <Typography>
          <b>데이터 과학(Data Science)</b>은 여러가지의 데이터들을 수집하고 분석하여 이를 통해
          문제를 해결하거나 결정을 내리는 과정을 말합니다. <br />
        </Typography>
      </Box>

      <Divider />
      <Box className="DataScienceGuideContent-section">
        <Box className="DataScienceGuideContent-title">테이블이란</Box>
        <Typography>
          <em>테이블(Table)</em>이란 데이터를 구조화하여 보기 쉽게 만드는 도구입니다.
          <br />
          테이블은 데이터를 <em>행(row)</em>과 <em>열(column)</em>로 나누어 데이터를 나타냅니다.
          <br />
          <br />
          테이블을 만드는 과정은 다음과 같습니다.
          <br />
          1. <b>데이터 수집</b>: 먼저, 분석할 데이터를 수집합니다. ex)학생들의 시험 성적
          <br />
          2. <b>테이블 구성</b>: 수집한 데이터로 행(row)과 열의(column) 형태로 테이블을 만듭니다.
          <ul>
            <li>
              <b>행(Rows)</b>: 각 데이터를 나열하는 부분입니다. 예를 들어, 각 학생의 성적 정보가 한
              행에 기록됩니다.
            </li>
            <li>
              <b>열(Columns)</b>: 데이터의 각 속성을 나타냅니다. 예를 들어, "이름", "수학 점수",
              "영어 점수" 같은 것들이 열이 됩니다.
            </li>
          </ul>
          이렇게 테이블을 만들면 데이터를 체계적으로 정리할 수 있어서, 분석이 훨씬 수월해집니다.
          <Box className="DataScienceGuideContent-imageBox">
            <img src="/_static/images/data_science_2.png" width={400} alt="" />
          </Box>
        </Typography>
        <Box className="DataScienceGuideContent-title">회귀분석이란</Box>
        <Typography>
          <em>회귀 분석</em>은 두 가지 다른 것들이 어떻게 연결되어 있는지 알아보는 방법입니다.
          <br />
          예를 들어, 매일 공부를 얼마나 했는지에 따라 시험 점수가 어떻게 되는지를 알고 싶을때,
          다음과 같이 회귀 분석을 사용하여 예측할 수 있습니다.
          <br />
          <br />
          1. <b>데이터 모으기</b>: 먼저, 몇 명의 친구들의 매일 공부한 시간과 시험 점수를 기록합니다.
          예를 들어, 친구 A는 하루에 1시간 공부하고 시험에서 80점을 받았고, 친구 B는 2시간 공부하고
          90점을 받았습니다.
          <br />
          <br />
          2. <b>그래프 그리기</b>: 이렇게 모은 정보를 바탕으로 그래프를 그려요. x축(가로축)에는
          공부한 시간을, y축(세로축)에는 시험 점수를 적습니다.
          <br />
          <br />
          3. <b>직선 그리기</b>: 그래프 위에 직선을 그립니다. 이 직선은 공부한 시간과 시험 점수
          사이의 관계를 보여줍니다. 예를 들어, 공부하는 시간이 늘어날수록 시험 점수가 높아지는
          경향이 있다면, 직선이 위로 올라가는 모양이 됩니다.
          <br />
          <br />
          4. <b>예측하기</b>: 이 직선을 사용해서 새로운 정보를 예측할 수 있습니다. 만약 한 친구가
          하루에 3시간 공부한다면, 직선을 보고 대충 얼마나 좋은 점수를 받을지 예측할 수 있습니다.
          <Box className="DataScienceGuideContent-imageBox">
            <img src="/_static/images/data_science_3.png" width={300} alt="" />
          </Box>
          <br />
          <br />
          회귀 분석은 이렇게 데이터를 통해 어떤 것들이 서로 어떻게 연결되어 있는지 알아보고, 이를
          바탕으로 미래의 상황을 예측하는 데 도움을 주는 방법입니다.
        </Typography>
        <Box className="DataScienceGuideContent-subtitle">단순 회귀 분석이란</Box>
        <Typography>
          <em>단순 회귀 분석</em>은 두 가지 변수 사이의 관계를 알아보는 회귀 분석 방법입니다.
          <br />
          예를 들어, 공부 시간과 시험 점수 사이의 관계를 분석할 때,
          <br />
          하나의 변수(공부 시간)가 다른 변수(시험 점수)에 어떤 영향을 미치는지 알아봅니다.
          <br />
          그래프에서는 두 변수 사이의 관계를 직선으로 나타냅니다.
        </Typography>
        <Box className="DataScienceGuideContent-subtitle">다중 회귀 분석이란</Box>
        <Typography>
          <em>다중 회귀 분석</em>은 여러 개의 변수들 사이의 관계를 동시에 분석하는 회귀 분석
          방법입니다.
          <br />
          예를 들어, 공부 시간, 수면 시간, 그리고 학교에서의 참여도 같은 여러 가지 요인이 시험
          점수에 미치는 영향을 모두 고려하고 싶을 때 사용합니다.
          <br />
          다중 회귀 분석을 통해 여러 변수들이 시험 점수에 어떤 영향을 미치는지 복잡하게 분석할 수
          있습니다. 그래프에서는 여러 차원에서 관계를 분석하기 때문에 직선이 아니라 평면이나 더
          복잡한 형태로 나타낼 수 있습니다.
        </Typography>

        <Box className="DataScienceGuideContent-subtitle">로지스틱 회귀 분석이란</Box>
        <Typography>
          <em>로지스틱 회귀분석</em>은 결과가 두 가지 선택 중 하나가 될 확률을 예측하는 회귀 분석
          방법입니다.
          <br />
          예를 들어 ‘시험을 통과할 확률’ 또는 ‘시험에 합격할 가능성’처럼, 결과가 '네' 또는
          '아니요'로 나올 때 사용합니다.
          <br />
          결과가 확률로 나타나며, 그래프는 일반적으로 S자 모양의 곡선(로지스틱 함수)으로 표현됩니다.
        </Typography>
      </Box>
    </Box>
  );
}
